import {
    Table,
    TableBody,
    TableContainer,
    Button,
    Alert,
    Menu,
    MenuItem,
    useTheme,
    Card,
    Stack
} from '@mui/material';
import { useUser } from '@/contexts/user/UserContext';
import {
    IntectUserDetailed,
    PaymentsUser,
    UserDetailed,
    ZenegyUserDetailed
} from '@/models/payment';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import Chip, { chipColors } from '@/components/shared/Chip';
import {
    TableCell,
    TableCellNoPadding,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellWithCheckbox,
    TableRow,
    TableSortLabel
} from '@/components/shared/styles/Table';
import { TablePagination } from '@/components/shared/TablePagination';
import { SMBold } from '@/typography/Typography';
import CardHeader from '@/components/shared/CardHeader';
import { Dropdown, DropdownType } from '@/components/shared/Dropdown';
import { Checkbox } from '@/components/shared/CheckBox';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { ExternalReference } from './UserTimeRegistrationRecordDetails';
import { UserRoles } from '@/models/company';
import { useEarningPermission } from '@/lib/hooks/useEarningPermission';

import { useOrdering } from '@/lib/hooks/useOrdering';

export enum UserTableType {
    Zenergy = 'zenergy',
    Intect = 'intect'
}

export type UsersTableProps<T = UserDetailed> = {
    usersDetailed: any;
    checked: string[];
    allChecked: boolean;
    onStatusClick: (userId: string, isActive: boolean) => void;
    onRoleToggleChange: (userId: string, isAdmin: boolean) => void;
    onRowClick: (userId: string) => () => void;
    onCheckAllClick: () => () => void;
    onEditClick: (userId: any) => void;
    onUpdateBankAccountDetailsClick: (userId: string) => () => void;
    onSendActivationMailClicked: (userId: string, name: string) => void;
    handleBulkEnable?: (checked: string[]) => void;
    handleBulkDisable?: (checked: string[]) => void;
    handleDeleteUser?: (userId: string) => void;
    total?: number;
    handleChangePage?: (
        event: React.MouseEvent<HTMLButtonElement> | null,
        page: number
    ) => void;
    handleChangeRowsPerPage?: (rowsPerPage: number) => void;
    rowsPerPage: number;
    userTableType: UserTableType;
    setChecked?: any;
    displayAdmin?: boolean;
    addUserForm?: any;
};

export const getStatusView = (
    isFirstLogin: boolean,
    isActive: boolean,
    isDisabled: boolean
) => {
    let text = 'Imported';
    let color = 'muted';

    if (isActive && !isFirstLogin && !isDisabled) {
        text = 'Active';
        color = 'success';
    } else if (isActive && isFirstLogin && !isDisabled) {
        text = 'Enabled';
        color = 'warning';
    }
    if (!isActive && isDisabled) {
        text = 'Disabled';
        color = 'error';
    }

    return <Chip label={text} color={color as any} />;
};

export const UsersTable = ({
    usersDetailed,
    total,
    handleBulkEnable,
    handleBulkDisable,
    handleChangePage,
    handleChangeRowsPerPage,
    rowsPerPage,
    handleDeleteUser,
    userTableType,
    checked,
    setChecked,
    displayAdmin,
    addUserForm,
    ...props
}: UsersTableProps) => {
    const { user } = useUser();
    const [checkedMap, setCheckedMap] = useState<{ [key: string]: boolean }>(
        {}
    );
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const [rowUser, setRowUser] = useState<PaymentsUser>();

    useEffect(() => {
        let map = {} as { [key: string]: boolean };

        checked.forEach((userId) => {
            map[userId] = true;
        });
        setCheckedMap(map);
    }, [checked]);

    const handleMenuClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        rowUser: PaymentsUser
    ) => {
        setAnchorEl(event.currentTarget);
        setRowUser(rowUser);
        setChecked([rowUser?.userId]);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        setChecked([]);
    };

    const theme = useTheme();

    const { t } = useTranslation();

    const getStatusButton = (id: string, isActive: boolean) => {
        const color = isActive ? 'success' : 'error';
        const text = isActive ? 'Disable' : 'Enable';

        return (
            <div
                style={{ width: '100%' }}
                onClick={() => {
                    handleMenuClose();
                    props.onStatusClick(id, !isActive);
                }}
                color={color}
            >
                {text}
            </div>
        );
    };

    const userList = useMemo(() => {
        if (userTableType == UserTableType.Zenergy) {
            return displayAdmin
                ? (usersDetailed?.companySettingsUsers as ZenegyUserDetailed[])
                : (usersDetailed?.users as ZenegyUserDetailed[]);
        } else {
            return displayAdmin
                ? (usersDetailed?.companySettingsUsers as IntectUserDetailed[])
                : (usersDetailed?.users as IntectUserDetailed[]);
        }
    }, [usersDetailed?.users, usersDetailed?.companySettingsUsers]);

    // Use ordering hook for sorting
    const {
        order,
        orderBy,
        handleRequestSort,
        orderedItems: sortedUserList
    } = useOrdering(
        (Array.isArray(userList) ? userList : []) as any[],
        'firstName',
        'asc'
    );

    const onHandleBulkEnableOrDisable = useCallback(
        (value: string) => {
            if (value === 'Enable') {
                handleBulkEnable?.(checked);
            }
            if (value === 'Disable') {
                handleBulkDisable?.(checked);
            }
        },
        [checked, handleBulkEnable]
    );

    const actions = (
        <Stack direction="row">
            {/* <OutlinedSecondaryButton>Actions</OutlinedSecondaryButton> */}
            <Dropdown
                type={DropdownType.Action}
                label="Action"
                listContainerStyle={{
                    left: '-40px',
                    width: '200px'
                }}
                choices={[
                    {
                        label: 'Enable Selected',
                        value: 'Enable'
                    },
                    {
                        label: 'Disable Selected',
                        value: 'Disable'
                    }
                ]}
                value={''}
                onChange={onHandleBulkEnableOrDisable}
            />
        </Stack>
    );

    const navigation = useNavigate();
    const navigateToUserDetails = (userId: string) => {
        navigation(`/users/${userId}`);
    };

    const { accessToUserEarningsAllowed } = useEarningPermission(user.role);

    return (
        <>
            <Card>
                <CardHeader
                    label={t('list')}
                    chip={
                        <Chip
                            icon={<span></span>}
                            label={
                                <SMBold color={chipColors.success.text}>
                                    {!displayAdmin
                                        ? `${
                                              userList && userList.length
                                          } of ${total}`
                                        : `${sortedUserList.length} of ${usersDetailed?.companySettingsUsers?.length}`}
                                </SMBold>
                            }
                            color={'success'}
                        />
                    }
                    actions={displayAdmin ? addUserForm : actions}
                />
                <TableContainer style={{ minWidth: '1424px' }}>
                    {userList && userList.length > 0 ? (
                        <Table aria-label="simple table">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        <Checkbox
                                            onClick={props.onCheckAllClick()}
                                            sx={{
                                                marginLeft: '10px',
                                                padding: 0
                                            }}
                                            edge="start"
                                            checked={props.allChecked}
                                            tabIndex={-1}
                                            inputProps={{
                                                style: {
                                                    borderRadius: '4px'
                                                },
                                                'aria-labelledby':
                                                    'checkbox-list-secondary-label-all'
                                            }}
                                        />
                                    </TableHeaderCell>
                                    <TableHeaderCellWithCheckbox>
                                        <TableSortLabel
                                            active={orderBy === 'firstName'}
                                            direction={
                                                orderBy === 'firstName'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleRequestSort('firstName')
                                            }
                                        >
                                            {t('name')}
                                        </TableSortLabel>
                                    </TableHeaderCellWithCheckbox>
                                    <TableHeaderCellWithCheckbox>
                                        <TableSortLabel
                                            active={orderBy === 'email'}
                                            direction={
                                                orderBy === 'email'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleRequestSort('email')
                                            }
                                        >
                                            {t('email')}
                                        </TableSortLabel>
                                    </TableHeaderCellWithCheckbox>
                                    <TableHeaderCellWithCheckbox>
                                        <TableSortLabel
                                            active={orderBy === 'phoneNumber'}
                                            direction={
                                                orderBy === 'phoneNumber'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleRequestSort('phoneNumber')
                                            }
                                        >
                                            {t('mobile')}
                                        </TableSortLabel>
                                    </TableHeaderCellWithCheckbox>
                                    {!displayAdmin && (
                                        <>
                                            <TableHeaderCellWithCheckbox
                                                sx={{
                                                    minWidth: '120px'
                                                }}
                                            >
                                                <TableSortLabel
                                                    active={
                                                        orderBy ===
                                                        'employmentType'
                                                    }
                                                    direction={
                                                        orderBy ===
                                                        'employmentType'
                                                            ? order
                                                            : 'asc'
                                                    }
                                                    onClick={() =>
                                                        handleRequestSort(
                                                            'employmentType'
                                                        )
                                                    }
                                                >
                                                    {t('salary_type')}
                                                </TableSortLabel>
                                            </TableHeaderCellWithCheckbox>
                                            <TableHeaderCellWithCheckbox>
                                                <TableSortLabel
                                                    active={
                                                        orderBy ===
                                                        'departments'
                                                    }
                                                    direction={
                                                        orderBy ===
                                                        'departments'
                                                            ? order
                                                            : 'asc'
                                                    }
                                                    onClick={() =>
                                                        handleRequestSort(
                                                            'departments'
                                                        )
                                                    }
                                                >
                                                    {t('department')}
                                                </TableSortLabel>
                                            </TableHeaderCellWithCheckbox>

                                            <TableHeaderCellWithCheckbox>
                                                <TableSortLabel
                                                    active={
                                                        orderBy ===
                                                        'employeeType'
                                                    }
                                                    direction={
                                                        orderBy ===
                                                        'employeeType'
                                                            ? order
                                                            : 'asc'
                                                    }
                                                    onClick={() =>
                                                        handleRequestSort(
                                                            'employeeType'
                                                        )
                                                    }
                                                >
                                                    {t('employee_type')}
                                                </TableSortLabel>
                                            </TableHeaderCellWithCheckbox>
                                        </>
                                    )}

                                    {displayAdmin && (
                                        <TableHeaderCellWithCheckbox>
                                            <TableSortLabel
                                                active={orderBy === 'role'}
                                                direction={
                                                    orderBy === 'role'
                                                        ? order
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleRequestSort('role')
                                                }
                                            >
                                                {t('access_type')}
                                            </TableSortLabel>
                                        </TableHeaderCellWithCheckbox>
                                    )}

                                    <TableHeaderCellWithCheckbox>
                                        {t('status')}
                                    </TableHeaderCellWithCheckbox>
                                    {!displayAdmin && (
                                        <>
                                            <TableHeaderCellWithCheckbox>
                                                <TableSortLabel
                                                    active={
                                                        orderBy ===
                                                        'timeRegistrationProviderExternalReference'
                                                    }
                                                    direction={
                                                        orderBy ===
                                                        'timeRegistrationProviderExternalReference'
                                                            ? order
                                                            : 'asc'
                                                    }
                                                    onClick={() =>
                                                        handleRequestSort(
                                                            'timeRegistrationProviderExternalReference'
                                                        )
                                                    }
                                                >
                                                    {t('external_reference')}
                                                </TableSortLabel>
                                            </TableHeaderCellWithCheckbox>
                                            <TableHeaderCellWithCheckbox>
                                                <TableSortLabel
                                                    active={orderBy === 'role'}
                                                    direction={
                                                        orderBy === 'role'
                                                            ? order
                                                            : 'asc'
                                                    }
                                                    onClick={() =>
                                                        handleRequestSort(
                                                            'role'
                                                        )
                                                    }
                                                >
                                                    {t('access_type')}
                                                </TableSortLabel>
                                            </TableHeaderCellWithCheckbox>
                                        </>
                                    )}

                                    <TableHeaderCellWithCheckbox></TableHeaderCellWithCheckbox>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedUserList?.map((rowUser) => (
                                    <TableRow
                                        style={{
                                            backgroundColor: checkedMap[
                                                rowUser.userId
                                            ]
                                                ? theme.palette.selectedRowColor
                                                : 'inherit'
                                        }}
                                        key={rowUser.userId}
                                        // onClick={props.onRowClick(rowUser.userId)}
                                    >
                                        <TableCell>
                                            {rowUser.userId !== user.id && (
                                                <Checkbox
                                                    sx={{
                                                        marginLeft: '10px',
                                                        padding: 0
                                                    }}
                                                    onClick={props.onRowClick(
                                                        rowUser.userId
                                                    )}
                                                    checked={
                                                        checkedMap[
                                                            rowUser.userId
                                                        ] ?? false
                                                    }
                                                    inputProps={{
                                                        'aria-labelledby': `checkbox-list-secondary-label-${rowUser.userId}`
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            onClick={() => {
                                                if (
                                                    accessToUserEarningsAllowed(
                                                        rowUser.role
                                                    ) &&
                                                    !displayAdmin
                                                ) {
                                                    navigateToUserDetails(
                                                        rowUser.userId
                                                    );
                                                } else {
                                                    props.onRowClick(
                                                        rowUser.userId
                                                    )();
                                                }
                                            }}
                                            style={
                                                accessToUserEarningsAllowed(
                                                    rowUser.role
                                                ) && !displayAdmin
                                                    ? {
                                                          cursor: 'pointer',
                                                          textDecoration:
                                                              'underline'
                                                      }
                                                    : {}
                                            }
                                        >
                                            {rowUser.firstName}{' '}
                                            {rowUser.lastName}
                                        </TableCell>
                                        <TableCell
                                            onClick={props.onRowClick(
                                                rowUser.userId
                                            )}
                                        >
                                            {rowUser.email}
                                        </TableCell>
                                        <TableCell
                                            onClick={props.onRowClick(
                                                rowUser.userId
                                            )}
                                        >
                                            {rowUser.phoneNumber}
                                        </TableCell>
                                        {!displayAdmin && (
                                            <>
                                                <TableCell>
                                                    {rowUser.employmentType}
                                                </TableCell>

                                                <TableCell
                                                    onClick={props.onRowClick(
                                                        rowUser.userId
                                                    )}
                                                >
                                                    {rowUser?.departments
                                                        ?.map(
                                                            (d) =>
                                                                d.departmentName
                                                        )
                                                        .join(', ')}
                                                    {!rowUser.departments && (
                                                        <LinkOffIcon titleAccess="This employee does not have a record for department and won't be able to use the App" />
                                                    )}
                                                </TableCell>
                                            </>
                                        )}
                                        {!displayAdmin && (
                                            <TableCell
                                                onClick={props.onRowClick(
                                                    rowUser.userId
                                                )}
                                            >
                                                {
                                                    rowUser?.employeeType
                                                        ?.employeeTypeName
                                                }
                                            </TableCell>
                                        )}

                                        {displayAdmin && (
                                            <TableCell
                                                onClick={props.onRowClick(
                                                    rowUser.userId
                                                )}
                                            >
                                                {rowUser.role}
                                            </TableCell>
                                        )}
                                        <TableCell
                                            onClick={props.onRowClick(
                                                rowUser.userId
                                            )}
                                        >
                                            {getStatusView(
                                                rowUser.isFirstLogin,
                                                rowUser.isActive,
                                                rowUser.isDisabled
                                            )}
                                        </TableCell>
                                        {!displayAdmin && (
                                            <>
                                                <TableCell
                                                    onClick={props.onRowClick(
                                                        rowUser.userId
                                                    )}
                                                >
                                                    {
                                                        <ExternalReference
                                                            salaryProviderExternalReference={
                                                                rowUser.salaryProviderExternalReference
                                                            }
                                                            timeRegistrationProviderExternalReference={
                                                                rowUser.timeRegistrationProviderExternalReference
                                                            }
                                                        />
                                                    }
                                                </TableCell>

                                                <TableCell
                                                    onClick={props.onRowClick(
                                                        rowUser.userId
                                                    )}
                                                >
                                                    {rowUser.role == 'User'
                                                        ? t('employee')
                                                        : rowUser.role}
                                                </TableCell>
                                            </>
                                        )}

                                        <TableCellNoPadding>
                                            {(rowUser.userId !== user.id ||
                                                user.role ===
                                                    UserRoles.SuperAdmin) && (
                                                <Button
                                                    color="secondary"
                                                    aria-controls={
                                                        open
                                                            ? 'basic-menu'
                                                            : undefined
                                                    }
                                                    aria-haspopup="true"
                                                    aria-expanded={
                                                        open
                                                            ? 'true'
                                                            : undefined
                                                    }
                                                    onClick={(event) => {
                                                        handleMenuClick(
                                                            event,
                                                            rowUser
                                                        );
                                                        props.onRowClick(
                                                            rowUser.userId
                                                        );
                                                    }}
                                                >
                                                    <MoreVertIcon />
                                                </Button>
                                            )}
                                        </TableCellNoPadding>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Alert sx={{ marginTop: 1 }} severity="info">
                            The user list is empty.
                        </Alert>
                    )}

                    <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        MenuListProps={{
                            'aria-labelledby': 'basic-button'
                        }}
                        sx={{ boxShadow: 'none' }}
                    >
                        <MenuItem>
                            {getStatusButton(
                                rowUser?.userId!,
                                rowUser?.isActive!
                            )}
                        </MenuItem>

                        {!displayAdmin && (
                            <Link
                                style={{
                                    textDecoration: 'none',
                                    color: 'black'
                                }}
                                to={{
                                    pathname: `/users/${rowUser?.userId}`
                                }}
                            >
                                <MenuItem
                                    onClick={() => {}}
                                    sx={{ boxShadow: 'none' }}
                                >
                                    {t('see_earnings')}
                                </MenuItem>
                            </Link>
                        )}

                        {!displayAdmin && (
                            <MenuItem
                                onClick={() => {
                                    props.onEditClick(rowUser?.userId!);
                                    handleMenuClose();
                                }}
                                aria-label="edit user"
                            >
                                {t('edit_details')}
                            </MenuItem>
                        )}

                        {!displayAdmin && (
                            <MenuItem
                                onClick={props.onUpdateBankAccountDetailsClick(
                                    rowUser?.userId!
                                )}
                                aria-label="edit bank account details"
                            >
                                {t('edit_bank')}
                            </MenuItem>
                        )}

                        {displayAdmin &&
                            (rowUser?.userId !== user.id ||
                                user.role === UserRoles.SuperAdmin) && (
                                <MenuItem
                                    onClick={() => {
                                        props.onEditClick(rowUser);
                                        handleMenuClose();
                                    }}
                                    aria-label="edit user"
                                >
                                    {t('edit_access')}
                                </MenuItem>
                            )}

                        <MenuItem
                            onClick={() => {
                                props.onSendActivationMailClicked(
                                    rowUser?.userId!,
                                    rowUser?.firstName + ' ' + rowUser?.lastName
                                );
                                handleMenuClose();
                            }}
                        >
                            {t('send_activation_mail')}
                        </MenuItem>

                        {(user.role === UserRoles.SuperAdmin ||
                            user.role === UserRoles.Admin) && (
                            <MenuItem
                                onClick={() => {
                                    handleDeleteUser?.(rowUser?.userId!);
                                    handleMenuClose();
                                }}
                            >
                                {t('delete_user')}
                            </MenuItem>
                        )}
                    </Menu>
                </TableContainer>
                <TablePagination
                    count={total!}
                    page={0}
                    onPageChange={() => {}}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage as any} //TODO: Fix this
                    rowsPerPageOptions={[
                        {
                            label: '16',
                            value: 16
                        },
                        {
                            label: '24',
                            value: 24
                        },
                        {
                            label: '32',
                            value: 32
                        },
                        {
                            label: 'All',
                            value: -1
                        }
                    ]}
                />
            </Card>
        </>
    );
};
